import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

export const query = graphql`
  fragment HeroStaticBackgroundWithLogosData on StrapiComponentHeroStaticBackgroundWithLogos {
    title
    topic
    description
    buttons {
      ...StrapiSharedLink
    }
    logos {
      ...StrapiSharedLink
    }
    backgroundMedia {
      ...StrapiMediaSubquery
    }
  }
`;

function StaticBackgroundWithLogos({ data }) {
  if (!data) {
    return <></>;
  }

  let highlightButton;
  let buttons;
  if (data.buttons) {
    buttons = [...data.buttons];
    highlightButton = buttons.shift();
  }

  return (
    <div className="relative overflow-hidden bg-background-black pt-20">
      <div className="absolute left-1/2 top-1/2 z-0 min-h-full min-w-full -translate-x-1/2 -translate-y-1/2">
        {data.backgroundMedia && (
          <MediaWrapper
            media={data.backgroundMedia}
            autoPlay
            loop
            muted
            disablePictureInPicture
            playsInline
            className="min-w-full max-w-none"
            divStyle={{
              zIndex: "0",
              position: "absolute",
              top: "50%",
              left: "50%",
              minHeight: "100%",
              minWidth: "100%",
              maxWidth: "none",
              transform: "translate(-50%, -50%)",
            }}
          />
        )}
      </div>
      <div className="relative z-10 mx-auto max-w-7xl px-4 lg:px-6">
        <div className="mx-auto w-full px-4 pb-20 pt-16 text-center lg:py-24 lg:text-left xl:pr-16">
          <div className="">
            {data.topic && (
              <h2 className="text-center font-semibold uppercase leading-6 tracking-wide text-text-on-primary">
                <MarkdownWrapper text={data.topic} />
              </h2>
            )}
            {data.title && (
              <h1 className="text-center text-4xl font-bold tracking-tight text-text-on-primary-light highlight-text sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl">
                <MarkdownWrapper
                  text={data.title}
                  className="block leading-relaxed xl:inline"
                />
              </h1>
            )}
            {data.description && (
              <MarkdownWrapper
                text={data.description}
                markdownClassName="mt-3 max-w-md mx-auto text-center text-lg text-text-on-primary sm:text-xl md:mt-5 md:max-w-3xl"
                replaceParagraph={false}
              />
            )}
            {Boolean(data.callToActions?.length) && (
              <div className="mt-10 sm:flex sm:justify-center">
                {highlightButton?.link?.url &&
                  (highlightButton?.overwriteTitle ||
                    highlightButton?.link?.title) && (
                    <div className="rounded-md shadow">
                      <LinkWrapper
                        to={highlightButton.link.url}
                        className="flex w-full items-center justify-center rounded-md border border-transparent bg-background-highlight px-8 py-3 text-base font-medium text-text-white custom-font hover:bg-background-highlight-dark md:px-10 md:py-4 md:text-lg"
                      >
                        {highlightButton?.overwriteTitle ||
                          highlightButton.link.title}
                      </LinkWrapper>
                    </div>
                  )}
                {buttons.map(
                  (button, index) =>
                    (button?.overwriteTitle || button?.link?.title) && (
                      <div
                        key={index}
                        className="mt-3 rounded-md shadow sm:ml-3 sm:mt-0"
                      >
                        <LinkWrapper
                          to={button.link?.url}
                          className="flex w-full items-center justify-center rounded-md border border-transparent bg-background-white px-8 py-3 text-base font-medium text-text-highlight custom-font hover:bg-background-off-white md:px-10 md:py-4 md:text-lg"
                        >
                          {button?.overwriteTitle || button.link.title}
                        </LinkWrapper>
                      </div>
                    )
                )}
              </div>
            )}
            {Boolean(data.logos?.length) && (
              <div className="mt-10 flex w-full flex-wrap justify-center gap-4">
                {data.logos.map(
                  (logo, index) =>
                    (logo.overwriteIcon || logo.link?.icon) && (
                      <div key={index} className="flex shrink-0 justify-center">
                        <LinkWrapper
                          to={logo?.link?.url}
                          className={`shrink-0 px-4 py-2`}
                        >
                          <MediaWrapper
                            media={logo.overwriteIcon || logo.link.icon}
                            divClassName="w-32 h-16 flex justify-center "
                            objectFit="contain"
                          />
                        </LinkWrapper>
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

StaticBackgroundWithLogos.propTypes = {
  data: PropTypes.object,
};

export default StaticBackgroundWithLogos;
